import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../utils/cn';
import { Icon, type IconName } from '../Icon';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Error message or boolean for error state */
  error?: string | boolean;
  /** Label text displayed above input */
  label?: string;
  /** Helper/hint text below input */
  helpText?: string;
  /** Alias for helpText */
  hint?: string;
  /** Icon to display on the left side */
  icon?: IconName | ReactNode;
  /** Icon to display on the right side */
  rightIcon?: IconName | ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, label, helpText, hint, icon, rightIcon, id, ...props }, ref) => {
    const inputId = id || (label ? `input-${Math.random().toString(36).substr(2, 9)}` : undefined);
    const hintText = helpText || hint;
    const hasLeftIcon = !!icon;
    const hasRightIcon = !!rightIcon;

    const renderIcon = (iconProp: IconName | ReactNode, position: 'left' | 'right') => {
      if (!iconProp) return null;
      if (typeof iconProp === 'string') {
        return (
          <Icon
            name={iconProp as IconName}
            size="sm"
            // GAP-015: text-mv-gray-500 for WCAG AA contrast
            className={cn(
              'absolute top-1/2 -translate-y-1/2 text-mv-gray-500',
              position === 'left' ? 'left-3' : 'right-3'
            )}
          />
        );
      }
      return (
        <span
          // GAP-015: text-mv-gray-500 for WCAG AA contrast
          className={cn(
            'absolute top-1/2 -translate-y-1/2 text-mv-gray-500',
            position === 'left' ? 'left-3' : 'right-3'
          )}
        >
          {iconProp}
        </span>
      );
    };

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-mv-gray-700">
            {label}
          </label>
        )}
        <div className="relative">
          {renderIcon(icon, 'left')}
          <input
            id={inputId}
            type={type}
            className={cn(
              'flex h-10 w-full rounded border border-mv-gray-300 bg-white px-3 py-2 text-sm',
              'placeholder:text-mv-gray-400',
              'focus:border-mv-blue-500 focus:outline-none focus:ring-2 focus:ring-mv-blue-500/20',
              'disabled:cursor-not-allowed disabled:bg-mv-gray-50 disabled:opacity-50',
              error && 'border-mv-error-500 focus:border-mv-error-500 focus:ring-mv-error-500/20',
              hasLeftIcon && 'pl-10',
              hasRightIcon && 'pr-10',
              className
            )}
            ref={ref}
            {...props}
          />
          {renderIcon(rightIcon, 'right')}
        </div>
        {error && typeof error === 'string' && <p className="mt-1 text-xs text-mv-error-500">{error}</p>}
        {hintText && !error && <p className="mt-1 text-xs text-mv-gray-500">{hintText}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';
